import { UntypedFormArray, UntypedFormBuilder, Validators } from "@angular/forms";
import { CONSTANT } from "src/app/constant";
import { CandidateRole } from "src/app/models/enum";
import {
	AssessmentConfigRes,
	ManeuverOutcomeDraft,
	OBComments,
	ReportSummary,
	ReportSummaryComment,
	TemCompetencyOutcomeDraft,
} from "src/app/models/models";
import {
	buildTs1ReviewCompetencyBlocks,
	buildTs1ReviewManeuverRows,
	isCompetencyManeuverCode,
	normalizeTemCompetencyOutcomes,
	resolveCrewRolesForCandidates,
	step3PfColumnOutcomeSlot,
	step3PmColumnOutcomeSlot,
	TS1_REVIEW_COMPETENCY_ORDER,
	Ts1ReviewCompetencyBlock,
	Ts1ReviewManeuverRow,
	Ts1ReviewRatingCell,
} from "../../ratings/container-ratings-form/ts1-rating-form-v3";
import {
	OutcomeSlot,
	outcomeSlotForObColumn,
	restoreV3OutcomesFromSelectedOBs,
} from "../../ratings/container-ratings-form/ts1-rating-form-v3";
import {
	buildLatestActiveOutcomeIndexMap,
	collapseEquivalentManeuverOutcomeComments,
	isReplaceableActiveOutcomeRow,
	MANEUVER_OUTCOME_NA_COMMENT,
	outcomeUpsertKey,
} from "../../ratings/container-ratings-form/ts1-rating-form-v3/utils/ts1-rating-outcome-interaction";
import { collapseTs1UndoneNaComments } from "../../ratings/container-ratings-form/ts1-rating-form-v3/preview/ts1-preview-summary-comments.utils";
import { Ts1PreviewCandidateDetails } from "../../ratings/container-ratings-form/ts1-rating-form-v3";
import { mapApiRowsToEts1SessionConfig } from "../../ets1/session/ets1-session-form.utils";
import { extractEts1SessionConfigurationsFromSelection } from "../../ets1/session/ets1-session-selection.utils";
import { buildFlightSimulatorDetailsPayload } from "../../ratings/container-ratings-form/shared-v2/flight-simulator-details.payload";
import { isTs1V3OrV4RatingFormVersion } from "../../ratings/container-ratings-form/shared-v2/utils/rating-form-version.utils";
import {
	createCandidateSignoffForm,
	createSignOffFormContainer,
} from "../../ratings/container-ratings-form/shared-v2/base/form.factories";

export interface Ts1ReportPreviewContext {
	candidateNames: string[];
	candidateIds: string[];
	candidateCrewRoles: CandidateRole[];
	maneuverOutcomes: ManeuverOutcomeDraft[];
	temCompetencyOutcomes: TemCompetencyOutcomeDraft[];
	competencyBlocks: Ts1ReviewCompetencyBlock[];
	maneuverRows: Ts1ReviewManeuverRow[];
	summaryComments: OBComments[];
	competencyObComments: OBComments[];
	candidateDetails: Ts1PreviewCandidateDetails | null;
	signOffForm: UntypedFormArray;
	generalObservation?: string;
	reminderNotes?: string;
	reminderNotesEnabled?: boolean;
	useStructuredReminderNotes?: boolean;
}

function toReviewCell(value: number | string | null | undefined): Ts1ReviewRatingCell {
	if (value == null || value === "") return null;
	if (value === "na" || value === "N/A") return "na";
	if (value === "inc" || value === "Inc.") return "inc";
	const num = Number(value);
	if (!Number.isFinite(num)) return null;
	if (num < 0) return "na";
	if (num === 0) return null;
	return num;
}

function isMissingReviewCell(value: Ts1ReviewRatingCell): boolean {
	return value == null;
}

function isOutcomeSlotUnset(outcome: OutcomeDraft, slot: OutcomeSlot): boolean {
	return readOutcomeSlotValue(outcome, slot) == null;
}

function resolveManeuverCode(
	comment: ReportSummaryComment,
	assessmentConfig: AssessmentConfigRes[],
): string | undefined {
	if (comment.maneuverCode) return comment.maneuverCode;

	const maneuverLabel = (comment.maneuver ?? "")
		.trim()
		.replace(/^R\d+\s+/i, "")
		.toUpperCase();
	if (!maneuverLabel) return undefined;

	const match = assessmentConfig.find((item) => {
		const code = (item.maneuverType?.code ?? "").toUpperCase();
		const description = (item.maneuverType?.description ?? "").toUpperCase();
		return maneuverLabel === description || maneuverLabel === code;
	});
	return match?.maneuverType?.code;
}

function mapSummaryComments(
	comments: ReportSummaryComment[] = [],
	assessmentConfig: AssessmentConfigRes[] = [],
): OBComments[] {
	return comments.map((comment) => {
		const isNaOutcome =
			!!comment.isManeuverOutcome &&
			(!!comment.isDisabled || !!comment.isNa || comment.remarks === "N/A");
		const instructorComment = resolveReportInstructorComment(comment);
		return {
			maneuver: comment.maneuver ?? "",
			maneuverCode: resolveManeuverCode(comment, assessmentConfig) ?? comment.maneuverCode ?? "",
			ob: comment.ob ?? "",
			action: comment.action ?? null,
			remarks: isNaOutcome ? comment.remarks?.trim() || "N/A" : comment.remarks,
			instructorComment,
			rate: comment.rate,
			candidateRole: comment.candidateRole ?? undefined,
			candidateId: comment.candidateId,
			isRepeat: comment.isRepeat,
			isReset: comment.isReset,
			isManeuverOutcome: comment.isManeuverOutcome,
			isTemCompetencyOutcome: comment.isTemCompetencyOutcome,
			isObservationComment: comment.isObservationComment,
			competencyCode: comment.competencyCode,
			isDisabled: comment.isDisabled ?? comment.isNa ?? false,
			lastActionDisabled: comment.lastActionDisabled ?? comment.undoNa ?? false,
			createdAt: comment.createdAt,
			repeatCount: comment.repeatCount,
		};
	});
}

/**
 * Hide OB `{ N/A }` rows while that seat's Maneuver Outcome is N/A — report parity with
 * live Step 2 timeline (cascade + prior manual N/A).
 */
export function shouldHideReportObNaUnderOutcomeNa(
	comment: Pick<
		OBComments,
		| "isManeuverOutcome"
		| "isTemCompetencyOutcome"
		| "isObservationComment"
		| "isDisabled"
		| "lastActionDisabled"
		| "ob"
		| "maneuverCode"
		| "candidateRole"
		| "candidateId"
	>,
	params: {
		maneuverOutcomes: ManeuverOutcomeDraft[];
		siblingCandidateIds: string[];
		hasSecondCandidate: boolean;
	},
): boolean {
	if (
		comment.isManeuverOutcome ||
		comment.isTemCompetencyOutcome ||
		comment.isObservationComment ||
		comment.ob === "REPEAT"
	) {
		return false;
	}
	if (comment.lastActionDisabled) return false;
	if (!comment.isDisabled) return false;

	const maneuverCode = String(comment.maneuverCode ?? "").trim();
	if (!maneuverCode) return false;

	const outcome = params.maneuverOutcomes.find((row) => row.maneuverCode === maneuverCode);
	if (!outcome) return false;

	const role =
		comment.candidateRole === CandidateRole.PM || comment.candidateRole === CandidateRole.PF
			? comment.candidateRole
			: null;
	if (!role) return false;

	const siblingIndex = comment.candidateId
		? params.siblingCandidateIds.indexOf(comment.candidateId)
		: -1;
	const candidateIndex = siblingIndex >= 0 ? siblingIndex : 0;
	const side: "left" | "right" = candidateIndex === 0 ? "left" : "right";
	const slot = outcomeSlotForObColumn(side, role, params.hasSecondCandidate);
	return outcome[slot] === "na";
}

function filterReportCommentsHiddenByOutcomeNa(
	comments: OBComments[],
	params: {
		maneuverOutcomes: ManeuverOutcomeDraft[];
		siblingCandidateIds: string[];
		hasSecondCandidate: boolean;
	},
): OBComments[] {
	return comments.filter((comment) => !shouldHideReportObNaUnderOutcomeNa(comment, params));
}

/** Remarks already show N/A — do not inject fixed "Maneuver Outcome N/A" instructor text. */
function resolveReportInstructorComment(comment: ReportSummaryComment): string {
	const text = String(comment.instructorComment ?? comment.comment ?? "").trim();
	const isNaOutcome =
		!!comment.isManeuverOutcome &&
		(!!comment.isDisabled || !!comment.isNa || comment.remarks === "N/A");
	if (isNaOutcome) return "";
	if (text.toUpperCase() === MANEUVER_OUTCOME_NA_COMMENT.toUpperCase()) return "";
	return text;
}

function toClearanceValue(value: boolean | string | null | undefined): boolean | string | undefined {
	if (value === null || value === undefined) return undefined;
	if (value === true || value === "true") return true;
	if (value === false || value === "false") return false;
	if (value === "N/A" || value === "n_a") return "N/A";
	return value;
}

function toRecommendationValue(value: boolean | string | null | undefined): boolean | undefined {
	if (value === true || value === "true" || value === "Yes" || value === "yes") return true;
	if (value === false || value === "false" || value === "No" || value === "no") return false;
	return undefined;
}

function resolveSiblingCandidateIds(
	preview: ReportSummary["ts1V3PreviewSummary"],
	fallbackCandidateId: string,
): string[] {
	if (preview?.siblingCandidateIds?.length) {
		return preview.siblingCandidateIds;
	}
	if (preview?.stagingCandidateIds?.length) {
		return preview.stagingCandidateIds;
	}
	return [fallbackCandidateId];
}

function resolvePreviewCrewRoles(
	preview: ReportSummary["ts1V3PreviewSummary"],
	candidateId: string,
): CandidateRole[] {
	const siblingCandidateIds = resolveSiblingCandidateIds(preview, candidateId);
	const crewRoleAssignment = preview?.crewRoleAssignment;
	const baseRoles = resolveCrewRolesForCandidates(
		siblingCandidateIds.map((id) => ({ candidateId: id })),
		crewRoleAssignment,
	);

	if (crewRoleAssignment?.rolesByCandidateIndex?.length) {
		return baseRoles;
	}

	if (crewRoleAssignment?.pfCandidateId || crewRoleAssignment?.pmCandidateId) {
		return baseRoles;
	}

	// Default carousel order — do not infer from summary rows because dual-crew grids
	// persist PF and PM column ratings on every candidate's outcome history.
	return siblingCandidateIds.map((_, index) =>
		index === 0 ? CandidateRole.PF : CandidateRole.PM,
	);
}

type OutcomeDraft = ManeuverOutcomeDraft | TemCompetencyOutcomeDraft;

function readOutcomeSlotValue(outcome: OutcomeDraft, slot: OutcomeSlot): ManeuverOutcomeDraft[OutcomeSlot] {
	return outcome[slot];
}

function writeOutcomeSlotValue(outcome: OutcomeDraft, slot: OutcomeSlot, value: OutcomeDraft[OutcomeSlot]): void {
	(outcome as Record<OutcomeSlot, OutcomeDraft[OutcomeSlot]>)[slot] = value;
}

/**
 * Completed summaries persisted via evaluations often store dual-crew ratings in legacy
 * `pfPrimary`/`pm` slots. Candidate 2's Step 3 view reads `pf`/`pmPrimary` instead.
 */
export function normalizeV3OutcomesForReportPreview<T extends OutcomeDraft>(
	outcomes: T[],
	candidateIndex: number,
	hasSecondCandidate: boolean,
): T[] {
	if (!hasSecondCandidate || candidateIndex !== 1) {
		return outcomes;
	}

	const ownSlot = step3PmColumnOutcomeSlot(candidateIndex, hasSecondCandidate);
	const partnerSlot = step3PfColumnOutcomeSlot(candidateIndex);
	const legacyOwnSlot: OutcomeSlot = "pm";
	const legacyPartnerSlot: OutcomeSlot = "pfPrimary";

	return outcomes.map((outcome) => {
		const next = { ...outcome };
		if (readOutcomeSlotValue(next, ownSlot) == null && readOutcomeSlotValue(next, legacyOwnSlot) != null) {
			writeOutcomeSlotValue(next, ownSlot, readOutcomeSlotValue(next, legacyOwnSlot));
		}
		if (readOutcomeSlotValue(next, partnerSlot) == null && readOutcomeSlotValue(next, legacyPartnerSlot) != null) {
			writeOutcomeSlotValue(next, partnerSlot, readOutcomeSlotValue(next, legacyPartnerSlot));
		}
		return next;
	});
}

function buildCompetencyBlocks(
	reportSummary: ReportSummary,
	temCompetencyOutcomes: TemCompetencyOutcomeDraft[],
	candidateIndex: number,
	hasSecondCandidate: boolean,
): Ts1ReviewCompetencyBlock[] {
	const preview = reportSummary.ts1V3PreviewSummary;
	const labels = reportSummary.chartData?.radarChartData?.labels ?? [];
	const datasets = reportSummary.chartData?.radarChartData?.datasets ?? [];
	const howManyData = datasets.find((d) => d.label === "How Many")?.data ?? datasets[0]?.data ?? [];
	const howOftenData = datasets.find((d) => d.label === "How Often")?.data ?? datasets[1]?.data ?? [];

	const howManyByCompetency = new Map<string, number>();
	const howOftenByCompetency = new Map<string, number>();
	labels.forEach((code, idx) => {
		howManyByCompetency.set(code, howManyData[idx] ?? 0);
		howOftenByCompetency.set(code, howOftenData[idx] ?? 0);
	});

	// Match Step 3: chart/calc can activate; TEM alone must not keep a block graded
	// (otherwise Rating C shows with blank A/B after N/A-only OB history).
	const fallbackApplicable = new Map(
		TS1_REVIEW_COMPETENCY_ORDER.map((code) => {
			const inChart = labels.includes(code);
			const hasCalc =
				(howManyByCompetency.get(code) ?? 0) > 0 || (howOftenByCompetency.get(code) ?? 0) > 0;
			return [code, inChart || hasCalc ? 1 : 0];
		}),
	);
	const serverApplicable = preview?.applicableByCompetency;
	const applicableByCompetency = new Map(
		TS1_REVIEW_COMPETENCY_ORDER.map((code) => {
			const serverVal = serverApplicable ? (Number(serverApplicable[code] ?? 0) > 0 ? 1 : 0) : 0;
			const fallbackVal = fallbackApplicable.get(code) ?? 0;
			return [code, serverVal || fallbackVal ? 1 : 0];
		}),
	);

	return buildTs1ReviewCompetencyBlocks({
		howManyByCompetency,
		howOftenByCompetency,
		applicableByCompetency,
		temCompetencyOutcomes,
		candidateIndex,
		hasSecondCandidate,
	});
}

/** Map API `maneuverRows` PF/PM columns onto the carousel slot model used by Step 3. */
function hydrateManeuverOutcomesFromPreviewRows(
	outcomes: ManeuverOutcomeDraft[],
	previewRows: Array<{ maneuverCode: string; pf?: Ts1ReviewRatingCell; pm?: Ts1ReviewRatingCell }>,
	candidateIndex: number,
	hasSecondCandidate: boolean,
): ManeuverOutcomeDraft[] {
	if (!previewRows.length) return outcomes;

	const pfSlot = step3PfColumnOutcomeSlot(candidateIndex);
	const pmSlot = step3PmColumnOutcomeSlot(candidateIndex, hasSecondCandidate);
	const byCode = new Map(outcomes.map((outcome) => [outcome.maneuverCode, { ...outcome }]));

	for (const row of previewRows) {
		if (!row.maneuverCode) continue;
		const outcome = byCode.get(row.maneuverCode) ?? { maneuverCode: row.maneuverCode };
		if (isOutcomeSlotUnset(outcome, pfSlot) && row.pf != null) {
			writeOutcomeSlotValue(outcome, pfSlot, row.pf);
		}
		if (isOutcomeSlotUnset(outcome, pmSlot) && row.pm != null) {
			writeOutcomeSlotValue(outcome, pmSlot, row.pm);
		}
		byCode.set(row.maneuverCode, outcome);
	}

	return [...byCode.values()];
}

function coercePreviewRatingValue(value: number | string | null | undefined): Ts1ReviewRatingCell {
	if (value == null || value === "") return null;
	if (value === "na" || value === "N/A") return "na";
	const num = Number(value);
	if (!Number.isFinite(num)) return null;
	if (num < 0) return "na";
	if (num === 0) return null;
	return num;
}

function previewManeuverRows(
	preview: ReportSummary["ts1V3PreviewSummary"],
): Array<{ maneuverCode: string; label: string; pf?: Ts1ReviewRatingCell; pm?: Ts1ReviewRatingCell }> {
	return (preview?.maneuverRows ?? [])
		.filter((row) => !!row?.maneuverCode && !isCompetencyManeuverCode(row.maneuverCode))
		.map((row) => ({
			maneuverCode: row.maneuverCode,
			label: row.label,
			pf: coercePreviewRatingValue(row.pf),
			pm: coercePreviewRatingValue(row.pm),
		}));
}

function buildManeuverRows(
	preview: ReportSummary["ts1V3PreviewSummary"],
	assessmentConfig: AssessmentConfigRes[],
	maneuverOutcomes: ManeuverOutcomeDraft[],
	candidateIndex: number,
	hasSecondCandidate: boolean,
): Ts1ReviewManeuverRow[] {
	const fallbackRows = previewManeuverRows(preview);
	const fallbackByCode = new Map(fallbackRows.map((row) => [row.maneuverCode, row]));

	if (assessmentConfig.length > 0) {
		const fromOutcomes = buildTs1ReviewManeuverRows({
			assessmentConfig,
			maneuverOutcomes,
			currentIndex: candidateIndex,
			hasSecondCandidate,
		});

		return fromOutcomes.map((row) => {
			const fallback = fallbackByCode.get(row.maneuverCode);
			if (!fallback) return row;
			return {
				...row,
				pf: isMissingReviewCell(row.pf) ? toReviewCell(fallback.pf) : row.pf,
				pm: isMissingReviewCell(row.pm) ? toReviewCell(fallback.pm) : row.pm,
			};
		});
	}

	return fallbackRows.map((row) => ({
		maneuverCode: row.maneuverCode,
		label: row.label,
		pf: toReviewCell(row.pf),
		pm: toReviewCell(row.pm),
	}));
}

export function dedupeTs1V3SummaryComments(comments: OBComments[]): OBComments[] {
	const collapsed = collapseTs1UndoneNaComments(comments);
	const latestOutcomeIdx = buildLatestActiveOutcomeIndexMap(collapsed);
	return collapsed.filter((row, index) => {
		if (!isReplaceableActiveOutcomeRow(row)) return true;
		const key = outcomeUpsertKey(row);
		return !key || latestOutcomeIdx.get(key) === index;
	});
}

function buildCandidateDetails(reportSummary: ReportSummary): Ts1PreviewCandidateDetails | null {
	const preview = reportSummary.ts1V3PreviewSummary;
	if (!preview?.candidateDetails) return null;

	const fs = preview.candidateDetails.flightSimulatorDetails ?? reportSummary.flightSimulatorDetails ?? {};
	const apiSessionConfigurations = preview.candidateDetails.sessionConfigurations ?? [];
	const ets1SessionCatalog = preview.ets1SessionCatalog
		? mapApiRowsToEts1SessionConfig(preview.ets1SessionCatalog)
		: undefined;
	const ets1SessionRemarksByCode = Object.fromEntries(
		apiSessionConfigurations
			.filter((cfg) => cfg.code && cfg.selectionRemarks)
			.map((cfg) => [cfg.code!, cfg.selectionRemarks!]),
	);
	const selectedSessionCodes = preview.selectedSessionCodes ?? [];
	const sessionConfigurations =
		ets1SessionCatalog?.length && selectedSessionCodes.length
			? extractEts1SessionConfigurationsFromSelection(
					ets1SessionCatalog,
					selectedSessionCodes,
					ets1SessionRemarksByCode,
				)
			: apiSessionConfigurations;

	return {
		...preview.candidateDetails,
		sessionConfigurations,
		flightSimulatorDetails: buildFlightSimulatorDetailsPayload(fs as Record<string, unknown>),
		ets1SessionCatalog,
		ets1SelectedSessionCodes: selectedSessionCodes,
		ets1SessionRemarksByCode,
		ets1SessionGroupCode: "ts_1",
	};
}

function buildReminderNoteResponsesFormArray(
	fb: UntypedFormBuilder,
	notes: Array<{
		noteId?: string;
		text?: string;
		requireStatus?: boolean;
		status?: string | null;
	}> = [],
): UntypedFormArray {
	return fb.array(
		notes.map((note) =>
			fb.group({
				noteId: [note.noteId ?? ""],
				text: [note.text ?? ""],
				requireStatus: [!!note.requireStatus],
				status: [note.status ?? null, note.requireStatus ? [Validators.required] : []],
			}),
		),
	);
}

function buildSignOffForm(fb: UntypedFormBuilder, reportSummary: ReportSummary, candidateId: string): UntypedFormArray {
	const container = createSignOffFormContainer(fb);
	const signOffArray = container.get("signOffForm") as UntypedFormArray;
	const preview = reportSummary.ts1V3PreviewSummary;
	const structuredReminderNotes = preview?.reminderNoteResponses ?? [];
	const hasStructuredReminderNotes = structuredReminderNotes.length > 0;
	const useStructuredReminderNotes = hasStructuredReminderNotes;
	const rec = reportSummary.recommendation;
	const session = reportSummary.sessionDetails;
	const category =
		rec?.sessionType?.toLowerCase() === "check" || rec?.sessionTypeDescription?.toLowerCase() === "check"
			? "Check"
			: "Training";

	const form = createCandidateSignoffForm(CONSTANT.FORM_TYPE.ts_1, fb, {
		candidateName: reportSummary.candidateDetails?.name ?? "",
		candidateId,
		traineeAssessmentId: candidateId,
		trainerName: session?.instructorName ?? previewInstructorName(reportSummary),
		signOffDetails: {
			ratingA: session?.ratingA ?? preview?.ratings?.ratingA ?? 0,
			ratingB: session?.ratingB ?? preview?.ratings?.ratingB ?? 0,
			ratingC: session?.ratingC ?? preview?.ratings?.ratingC ?? 0,
			finalRating: session?.finalRating ?? preview?.ratings?.finalRating ?? 0,
			category,
			sessionCheck: toRecommendationValue(rec?.is_complete) as unknown as string | undefined,
			objectiveCheck: toRecommendationValue(rec?.is_obj_met) as unknown as string | undefined,
			clearanceCheck: toClearanceValue(rec?.is_clear) as unknown as string,
			traineeName: reportSummary.candidateDetails?.name ?? "",
			trainerName: session?.instructorName ?? previewInstructorName(reportSummary),
			generalObservation:
				reportSummary.trainerExaminerNotes?.generalObservation ??
				preview?.trainerExaminerNotes?.generalObservation ??
				"",
			reminderNotes:
				reportSummary.trainerExaminerNotes?.reminderNotes ??
				preview?.trainerExaminerNotes?.reminderNotes ??
				"",
		},
	});

	if (useStructuredReminderNotes) {
		form.setControl(
			"reminderNoteResponses",
			buildReminderNoteResponsesFormArray(fb, structuredReminderNotes),
		);
	}

	signOffArray.push(form, { emitEvent: false });
	return signOffArray;
}

function previewInstructorName(reportSummary: ReportSummary): string {
	return reportSummary.ts1V3PreviewSummary?.instructorName ?? reportSummary.sessionDetails?.instructorName ?? "";
}

export function isTs1PfPmReportSummary(reportSummary?: ReportSummary): boolean {
	if (!reportSummary) return false;
	return (
		reportSummary.formType === CONSTANT.FORM_TYPE.ts_1 &&
		isTs1V3OrV4RatingFormVersion(reportSummary.formVersion) &&
		!!reportSummary.ts1V3PreviewSummary
	);
}

/** @deprecated Use {@link isTs1PfPmReportSummary} */
export function isTs1V3ReportSummary(reportSummary?: ReportSummary): boolean {
	return isTs1PfPmReportSummary(reportSummary);
}

export function buildTs1ReportPreviewContext(
	reportSummary: ReportSummary,
	fb: UntypedFormBuilder,
	candidateId: string,
): Ts1ReportPreviewContext {
	if (!isTs1PfPmReportSummary(reportSummary)) {
		throw new Error("buildTs1ReportPreviewContext is only supported for TS-1 PF/PM form versions 3 and 4");
	}

	const preview = reportSummary.ts1V3PreviewSummary;
	const candidateName = preview?.candidateDetails?.name ?? reportSummary.candidateDetails?.name ?? "";
	const assessmentConfig = ((preview?.assessmentConfig ?? []) as AssessmentConfigRes[]).filter(
		(item) => !!item?.maneuverType?.code && !isCompetencyManeuverCode(item.maneuverType.code),
	);
	const acceptedCandidateIds = new Set(
		resolveSiblingCandidateIds(preview, candidateId),
	);
	const summaryComments = mapSummaryComments(preview?.summaryComments ?? [], assessmentConfig);
	const competencyObComments = mapSummaryComments(
		(preview?.competencyObComments as ReportSummaryComment[] | undefined) ??
			preview?.summaryComments ??
			[],
		assessmentConfig,
	);
	const filteredSummaryComments = dedupeTs1V3SummaryComments(
		summaryComments.filter(
			(comment) =>
				comment.ob === "REPEAT" ||
				!comment.candidateId ||
				acceptedCandidateIds.has(comment.candidateId),
		),
	);
	const filteredCompetencyObComments = dedupeTs1V3SummaryComments(
		competencyObComments.filter(
			(comment) =>
				comment.ob === "REPEAT" ||
				!comment.candidateId ||
				acceptedCandidateIds.has(comment.candidateId),
		),
	);
	const outcomeCommentSource = filteredCompetencyObComments.length
		? filteredCompetencyObComments
		: filteredSummaryComments;
	const restoredOutcomes = restoreV3OutcomesFromSelectedOBs(outcomeCommentSource);
	const storedManeuverOutcomes = (preview?.maneuverOutcomes ?? []) as ManeuverOutcomeDraft[];
	const storedTemOutcomes = (preview?.temCompetencyOutcomes ?? []) as TemCompetencyOutcomeDraft[];
	const candidateIndex = preview?.candidateIndex ?? 0;
	const hasSecondCandidate = preview?.hasSecondCandidate ?? false;
	const candidateCount = hasSecondCandidate ? 2 : 1;
	const fallbackManeuverRows = previewManeuverRows(preview);
	const maneuverOutcomesBase = storedManeuverOutcomes.length
		? storedManeuverOutcomes
		: restoredOutcomes.maneuverOutcomes;
	const maneuverOutcomes = normalizeV3OutcomesForReportPreview(
		hydrateManeuverOutcomesFromPreviewRows(
			maneuverOutcomesBase,
			fallbackManeuverRows,
			candidateIndex,
			hasSecondCandidate,
		),
		candidateIndex,
		hasSecondCandidate,
	);
	const temCompetencyOutcomes = normalizeTemCompetencyOutcomes(
		storedTemOutcomes,
		candidateCount,
		candidateIndex,
	);
	const crewRoles = resolvePreviewCrewRoles(preview, candidateId);
	const candidateCrewRole = crewRoles[candidateIndex] ?? CandidateRole.PF;
	const collapseOutcomeDuplicates = (comments: OBComments[]) =>
		collapseEquivalentManeuverOutcomeComments(comments, {
			crewRoles,
			candidateIndex,
		});

	const siblingCandidateIds = resolveSiblingCandidateIds(preview, candidateId);
	const outcomeNaFilter = {
		maneuverOutcomes,
		siblingCandidateIds,
		hasSecondCandidate,
	};
	const summaryCommentsForDisplay = filterReportCommentsHiddenByOutcomeNa(
		filteredSummaryComments,
		outcomeNaFilter,
	);
	const competencyCommentsForDisplay = filterReportCommentsHiddenByOutcomeNa(
		filteredCompetencyObComments.length
			? filteredCompetencyObComments
			: filteredSummaryComments,
		outcomeNaFilter,
	);

	const maneuverRows = buildManeuverRows(
		preview,
		assessmentConfig,
		maneuverOutcomes,
		candidateIndex,
		hasSecondCandidate,
	);
	const hasStructuredReminderNotes = (preview?.reminderNoteResponses?.length ?? 0) > 0;
	const legacyReminderNotes =
		reportSummary.trainerExaminerNotes?.reminderNotes ?? preview?.trainerExaminerNotes?.reminderNotes;

	return {
		candidateNames: [candidateName],
		candidateIds: [candidateId],
		candidateCrewRoles: [candidateCrewRole],
		maneuverOutcomes,
		temCompetencyOutcomes,
		competencyBlocks: buildCompetencyBlocks(
			reportSummary,
			temCompetencyOutcomes,
			candidateIndex,
			hasSecondCandidate,
		),
		maneuverRows,
		summaryComments: collapseOutcomeDuplicates(summaryCommentsForDisplay),
		competencyObComments: collapseOutcomeDuplicates(competencyCommentsForDisplay),
		candidateDetails: buildCandidateDetails(reportSummary),
		signOffForm: buildSignOffForm(fb, reportSummary, candidateId),
		generalObservation:
			reportSummary.trainerExaminerNotes?.generalObservation ??
			preview?.trainerExaminerNotes?.generalObservation,
		reminderNotes:
			reportSummary.trainerExaminerNotes?.reminderNotes ??
			preview?.trainerExaminerNotes?.reminderNotes,
		reminderNotesEnabled:
			!!preview?.reminderNotesEnabled || hasStructuredReminderNotes || !!legacyReminderNotes?.trim(),
		useStructuredReminderNotes: hasStructuredReminderNotes,
	};
}
